/**
 * Org-wide "email open tracking" toggle, rendered as a row in the Email
 * settings list.
 *
 * Lives with the other outgoing-email settings (signature, footer, templates)
 * rather than on Email & Calendar: that section is about *connecting accounts*,
 * and a switch governing what Ahoy injects into every outgoing message had
 * nothing to do with the provider cards it sat under.
 *
 * Renders nothing unless the feature flag is on AND the viewer is an admin —
 * this is a workspace-wide setting, so a non-admin must not see a control they
 * cannot action.
 */

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermissions } from '@/hooks/usePermissions';
import { ButtonList } from '@/lib/base/button-list';
import { Toggle } from '@/lib/base/forms/toggle';
import { QueryErrorState } from '@/lib/components';
import { apiGet, apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import { BILLING_ADMIN_PERMISSION } from '../../settings-permissions';

interface EmailOpenTrackingRowProps {
  orgId: number | undefined;
}

export function EmailOpenTrackingRow({ orgId }: EmailOpenTrackingRowProps) {
  const trackingFlagEnabled = useFeatureFlag('email_pixel_tracking');
  const { hasPermissionGroup } = usePermissions();
  const isAdmin = hasPermissionGroup(BILLING_ADMIN_PERMISSION);
  const showTrackingToggle = trackingFlagEnabled && isAdmin;

  const [trackingEnabled, setTrackingEnabled] = useState<boolean | null>(null);
  const [isToggling, setIsToggling] = useState(false);
  const [fetchError, setFetchError] = useState<unknown>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!showTrackingToggle || orgId === undefined) {
      return;
    }
    setTrackingEnabled(null);
    setFetchError(null);
    const controller = new AbortController();
    apiGet<{ email_tracking_enabled: boolean }>(
      `/api/orgs/${orgId}/email-tracking/`,
      undefined,
      undefined,
      controller.signal
    )
      .then(response => {
        setTrackingEnabled(response.email_tracking_enabled);
      })
      .catch(error => {
        if (error?.name !== 'AbortError') {
          logger.error('Failed to fetch email tracking setting', error, {
            orgId,
          });
          setFetchError(error);
        }
      });
    return () => {
      controller.abort();
    };
  }, [showTrackingToggle, orgId, retryKey]);

  const handleRetry = useCallback(() => setRetryKey(k => k + 1), []);

  const handleToggle = useCallback(
    (newValue: boolean) => {
      const previousValue = trackingEnabled;
      setTrackingEnabled(newValue);
      setIsToggling(true);
      apiPost<{ email_tracking_enabled: boolean }>(
        `/api/orgs/${orgId}/email-tracking/`,
        { email_tracking_enabled: newValue }
      )
        .then(() => {
          toast.success(
            newValue ? 'Email tracking enabled' : 'Email tracking disabled'
          );
        })
        .catch(error => {
          setTrackingEnabled(previousValue);
          toast.error("We couldn't update the tracking setting. Try again.");
          logger.error('Failed to toggle email tracking', error, { orgId });
        })
        .finally(() => {
          setIsToggling(false);
        });
    },
    [orgId, trackingEnabled]
  );

  if (!showTrackingToggle || orgId === undefined) {
    return null;
  }

  if (fetchError !== null) {
    return (
      <ButtonList.Item>
        <QueryErrorState
          error={fetchError}
          label="We couldn't load the email tracking setting"
          onRetry={handleRetry}
          size='sm'
        />
      </ButtonList.Item>
    );
  }

  // Still loading — render nothing rather than a toggle in a guessed position,
  // which would flip under the user if the stored value turns out to be ON.
  if (trackingEnabled === null) {
    return null;
  }

  return (
    <ButtonList.Item
      trailing={
        <Toggle
          isSelected={trackingEnabled}
          onChange={handleToggle}
          isDisabled={isToggling}
          aria-label='Toggle email open tracking'
        />
      }
    >
      <p className='text-sm font-medium text-fg-primary mb-0.5'>
        Email open tracking
      </p>
      <p className='text-sm text-fg-tertiary'>
        Adds a tracking pixel to outgoing emails to detect when recipients open
        them. Individual emails can still opt out.
      </p>
      {/* Every other row in this list is a personal preference, so the one
          workspace-wide switch has to say so or an admin will assume they are
          only changing their own behaviour. */}
      <p className='text-xs text-fg-quaternary mt-1'>
        Applies to everyone in this workspace.
      </p>
    </ButtonList.Item>
  );
}
